import { cardHeight, cardWidth } from "@components/game/table/Card"
import { viewport } from "@telegram-apps/sdk"
import {
  Context,
  createContext,
  PropsWithChildren,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react"

interface SafeArea {
  topSafeArea: number
  bottomSafeArea: number
}

interface DimensionProps {
  cardHeight: number
  cardScale: number
  cardWidth: number
  height: number
  isHorizontal: boolean
  playerSize: number
  width: number
  isDark: boolean
  navbarReference: RefObject<HTMLDivElement>
  safeArea: SafeArea
}

export const DimensionContext: Context<DimensionProps> =
  createContext<DimensionProps>({} as DimensionProps)

export function DimensionProvider({ children }: PropsWithChildren) {
  const [isDark, setIsDark] = useState(false)
  const [dimensions, setDimensions] = useState<
    Omit<DimensionProps, "isDark" | "navbarReference" | "safeArea">
  >({
    cardHeight,
    cardScale: 1,
    cardWidth,
    height: window.innerHeight,
    isHorizontal: window.innerWidth > window.innerHeight,
    playerSize: 40,
    width: window.innerWidth,
  })
  const [safeArea, setSafeArea] = useState({
    topSafeArea: 0,
    bottomSafeArea: 0,
  })
  const navbarReference = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      const cardScale = window.innerHeight / 3.2 / cardHeight
      // window.innerWidth > window.innerHeight
      //   ? window.innerHeight / 2.6 / cardHeight
      //   : window.innerHeight / 3.2 / cardHeight

      setDimensions({
        cardHeight: cardScale * cardHeight,
        cardScale,
        cardWidth: cardScale * cardWidth,
        height: window.innerHeight,
        isHorizontal: false,
        playerSize: (cardHeight * cardScale) / 2.7,
        width: window.innerWidth,
      })
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const updateDarkMode = () => {
      setIsDark(document.documentElement.classList.contains("dark"))
    }

    updateDarkMode()

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          updateDarkMode()
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const handleSafeArea = () => {
      const rootStyles = getComputedStyle(document.documentElement)

      const insetTop1 =
        parseFloat(
          rootStyles.getPropertyValue(
            "--tg-viewport-content-safe-area-inset-top"
          )
        ) || viewport.safeAreaInsetTop()

      const insetTop2 =
        parseFloat(
          rootStyles.getPropertyValue("--tg-viewport-safe-area-inset-top")
        ) || viewport.contentSafeAreaInsetTop()

      const insetBottom =
        parseFloat(
          rootStyles.getPropertyValue(
            "--tg-viewport-content-safe-area-inset-bottom"
          )
        ) || viewport.safeAreaInsetBottom()

      const insetTop = insetTop1 + insetTop2

      setSafeArea({ topSafeArea: insetTop, bottomSafeArea: insetBottom })
    }

    handleSafeArea()
    window.addEventListener("resize", handleSafeArea)
    return () => window.removeEventListener("resize", handleSafeArea)
  }, [])

  return (
    <DimensionContext.Provider
      value={{ ...dimensions, isDark, navbarReference, safeArea }}
    >
      {children}
    </DimensionContext.Provider>
  )
}
